<template>
    <main>
        <section class="md:grid md:grid-cols-12">
            <div class="px-3.75 md:col-start-2 md:col-span-10 lg:col-start-3 lg:col-span-8">
                <div class="py-7.5" v-if="!hasAccesToken" v-html="$t('views.show_interest.register_account_first_text')"></div>
                <div class="py-7.5" v-else-if="!requestWasSent">
                    <h1 class="text-38 lg:text-60 mb-7.5">{{ $t('views.show_interest.headline') }}</h1>
                    <table class="mb-7.5">
                        <tr>
                            <td width="200"><label class="input-item__label">{{ $t('area') }}</label></td>
                            <td>{{ estate ? estate.border_names : ''}}</td>
                        </tr>
                        <tr>
                            <td><label class="input-item__label">{{ $t('street_address') }}</label></td>
                            <td>{{ estate ? `${estate.street_address} ${estate.street_number}` : '' }}</td>
                        </tr>
                        <tr>
                            <td><label class="input-item__label">{{ $t('room') }}</label></td>
                            <td>{{ estate ? `${estate.rooms} ${$t('room').toLocaleLowerCase()}` : '' }}</td>
                        </tr>
                        <tr>
                            <td><label class="input-item__label">{{ $t('size_sqm') }}</label></td>
                            <td>{{ estate ? estate.sqm : '' }}</td>
                        </tr>
                        <tr>
                            <td><label class="input-item__label">{{ $t('rent') }}</label></td>
                            <td>{{  estate ? `${estate.advertised_rent} SEK` : '' }}</td>
                        </tr>
                        <tr>
                            <td><label class="input-item__label">{{ $t('object') }}</label></td>
                            <td>{{ estate ? estate.object_number : '' }}</td>
                        </tr>
                    </table>
                    <form class="former" @submit.prevent="submit">
                        <div class="row">
                            <div class="row__col">
                                <BaseSelect
                                    v-model="tenant_id"
                                    :label="`${ $t('views.show_interest.choose_person_label') }`"
                                    :options="tenantOpt"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="row__col">
                                <BaseTextarea
                                    v-model="note"
                                    :label="$t('views.show_interest.note_label')"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="row__col former__btn former__btn--center">
                                <button
                                    type="submit"
                                    class="btn btn--pink"
                                    :class="{
                                        'btn--disabled': !tenant_id || tenant_id === '0'
                                    }"
                                >
                                    {{ $t('views.profile.btnSave') }}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
                <div class="py-7.5" v-else>
                    <h1 class="text-38 lg:text-60 mb-7.5">{{ $t('views.show_interest.request_was_sent_headline') }}</h1>
                    <p>{{ $t('views.show_interest.request_was_sent_text') }}</p>
                </div>
            </div>
        </section>
    </main> 
</template>

<script lang="ts">
    import { computed, defineComponent, reactive, ref, watch, onMounted, toRefs } from 'vue'
    import { useStore }                  from 'vuex'
    import { useRoute, useRouter }       from 'vue-router'
    import globalStoreMutations          from '@constans/store/global/globalStoreMutations'
    import estateStoreActions            from '@constans/store/estate/estateStoreActions'
    import estateStoreGetters            from '@constans/store/estate/estateStoreGetters'
    import companyStoreGetters           from '@constans/store/company/companyStoreGetters'
    import companyStoreActions           from '@constans/store/company/companyStoreActions'
    import authStoreGetters              from '@constans/store/auth/authStoreGetters'
    import {companyRegisterInterest}     from '@services/user/userDataService'
    import Former                        from '@components/form/Former.vue'
    import BaseSelect                    from '@components/form/BaseSelect.vue'
    import BaseTextarea                  from '@components/form/BaseTextarea.vue'

    export default defineComponent({
        components: {
            Former,
            BaseSelect,
            BaseTextarea,
        },
        setup() {
            const router = useRouter()
            const route  = useRoute()
            const store  = useStore()

            const tenantList = computed(() => store.getters[`company/${ companyStoreGetters.LIST_ACTIVE_TENANTS }`])
            const tenantOpt = ref([])
            const estate = computed(() => store.getters[`estate/${ estateStoreGetters.GET_SINGLE_ESTATE }`])
            const hasAccesToken = computed(() => store.getters[`auth/${ authStoreGetters.GET_ACCESS_TOKEN }`])
            let requestWasSent = ref(false)

            const payload = reactive({
                tenant_id: '',
                estate_id: '',
                company_id: '',
                note: '',
            })

            const submit = () => {
                sendRequest(payload).then(res => {
                        if (res.status === 'Success') {
                            requestWasSent.value = true
                        }

                        if (res.status === 'Error') {
                            alert('Något gick fel!')
                        }
                    })
            }

            const sendRequest = async (payload: any) => {
                const result = await companyRegisterInterest(payload).then(response => {
                    if (response.status === 'Success') {
                        return response
                    }
                }).catch(err => {
                    if (err.status === 'Error') {
                        return err
                    }
                })

                return result
            }

            watch(tenantList, () => {
                tenantOpt.value = tenantList.value.map((tenant: any) => {
                    return {
                        value: tenant.id,
                        name: `${tenant.first_name} ${ tenant.surname }`,
                    }
                })

                if (tenantList.value.length > 0) {
                    payload.company_id = tenantList.value[0].company_id
                }
            })

            watch(estate, () => {
                payload.estate_id = estate.value.id
            })

            onMounted(async () => {
                store.commit(`global/${ globalStoreMutations.UPDATE_LOADER }`, true)

                await store.dispatch(`company/${ companyStoreActions.GET }`)

                await store.dispatch(`estate/${ estateStoreActions.GET_ESTATE_FROM_UUID }`, route.params.estateId).then(OK => {
                    store.commit(`global/${ globalStoreMutations.UPDATE_LOADER }`, false)

                    if (!OK) {
                        router.push({ name: '404' })
                    }
                })
            })

            return {
                estate,
                ...toRefs(payload),
                hasAccesToken,
                tenantOpt,
                submit,
                requestWasSent
            }
        }
    })
</script>